/**
 * Halloween branding — the walking cat (imperative overlay).
 *
 * Mounts a transparent alpha-video cat into the root HalloweenCat renders and
 * walks it across the screen along the top edge of the bottom navigation bar
 * (or the status bar when the nav is off screen). Pure DOM + a single looping
 * transparent video (VP9-alpha WebM, or HEVC-alpha on WebKit — see
 * `pickSource`) — no canvas, no sprite sheets, no animation libraries.
 *
 * Lifecycle: module-level singleton, `mount()` is idempotent (no-op when
 * already mounted), `destroy()` removes the DOM and tears down every timer /
 * rAF / listener so a React remount (StrictMode, branding switch) can't leak a
 * second cat or a runaway loop.
 *
 * Anatomy:
 *   - The root (owned by HalloweenCat.tsx) is `position:fixed; inset:0;
 *     pointer-events:none` so the cat never intercepts a lobby click.
 *   - A `.cat` wrapper is translated to y and an inner `.cat-x` wrapper (around
 *     the video) is translated to x and flipped on the X axis to face its
 *     travel direction — matrix order translateY · translateX · scaleX, the
 *     same as the former single `translate(x, y) scaleX(f)`. X is a Web
 *     Animations API transform animation on `.cat-x` (linear, distance /
 *     walkSpeed), so it advances on the compositor in lock-step with the
 *     video even while the main thread is busy (navigation, search filters,
 *     modals, fast scroll) — a missed frame no longer makes a planted paw
 *     slide back and skip forward. Y is still written per frame by a rAF loop
 *     (see below). Engines without `Element.animate` fall back to integrating
 *     X in that same loop. A two-mode state machine drives it.
 *     `walking`: the cat enters from one screen edge, walks along the top edge
 *     of the navigation bar (Y is re-read from the live bar every
 *     LINE_READ_EVERY_FRAMES frames, so it rides the bar as it slides) and
 *     keeps going until it is fully off the opposite edge — strictly
 *     horizontal, never diagonal, never to a random point. On phones the nav
 *     bar slides away while the user scrolls, and the search view covers it on
 *     every device; the cat then drops onto the status bar instead (see
 *     `resolveWalkLine`). If neither bar is on screen and exposed mid-walk it
 *     keeps its line and simply walks off. `hidden`: the cat is off screen
 *     with the video's source released (the decoder with it — see `goHidden`)
 *     for a random few seconds, then re-enters from the edge it left through,
 *     walking the other way (re-checking for an exposed bar every IDLE_POLL_MS
 *     until one is); `tryEnter` hands the source back and reloads the clip.
 *   - Only `walking` runs a requestAnimationFrame loop (Y only: it re-reads
 *     the bar line every LINE_READ_EVERY_FRAMES frames); `hidden` is a single
 *     timeout. The loop and the X animation are also stopped — and the cat
 *     hidden, video released — while `setPaused(true)` (App.tsx's idle
 *     manager) or a hidden tab is in effect; she re-enters after
 *     FIRST_ENTRY_MS once that lifts.
 *   - The cat body is a single `<video>` element: a seamless-loop,
 *     transparent, side-on cat that faces RIGHT by default. Because the source
 *     faces right, the wrapper's `scaleX(facing)` flip is un-mirrored
 *     (facing=1) when walking right and mirrored (facing=-1) when walking
 *     left. She is kept invisible until the video fires `playing` (the X
 *     animation is created paused on entry and released then), so a frozen
 *     first frame is never shown; if playback never starts (autoplay blocked
 *     for good) she simply never appears.
 *
 * Source clips (apps/assets/public/assets/portal/brandings/halloween/):
 *   - `cat_walk.webm`: VP9 WebM with an alpha plane (`ALPHA_MODE=1`), ~100 KB.
 *     Chromium / Firefox render the transparency; WebKit does NOT — Safari and
 *     every iOS browser decode VP9 without the alpha plane and paint the
 *     transparent background as an opaque black box.
 *   - `cat_walk.mov`: HEVC (`hvc1`) QuickTime with Apple's alpha layer, encoded
 *     via VideoToolbox, ~140 KB. The only transparent-video format WebKit
 *     renders; Chromium plays it opaque, so it is picked for WebKit only.
 *   Both are 448x252, 24 fps, 32 frames = 1.333s seamless loops, made from the
 *   designer's 32-frame transparent PNG sequence (cat_01.png … cat_32.png). The
 *   cat faces right, her paws sit at y≈240 of 252, and a planted paw moves 8
 *   source px per frame (the stride the crossing speed is derived from).
 *   Re-encode with:
 *     ffmpeg -framerate 24 -i cat_%02d.png -c:v libvpx-vp9 -pix_fmt yuva420p \
 *       -auto-alt-ref 0 -b:v 0 -crf 32 -an cat_walk.webm
 *   and, on macOS, the HEVC:
 *     ffmpeg -framerate 24 -i cat_%02d.png -c:v hevc_videotoolbox -alpha_quality 0.75 \
 *       -q:v 60 -tag:v hvc1 -pix_fmt yuva420p -movflags +faststart -an cat_walk.mov
 */

import { isTrueMobile } from '../../utils/isTrueMobile';

/** Absolute URLs of the two encodes (built by HalloweenCat from the assets host). */
export interface CatSources {
  webm: string;
  hevc: string;
}

const CAT_CLASS = 'halloween-cat';
const CAT_X_CLASS = 'halloween-cat-x';

/** Source clip geometry: 448x252 at 24 fps. */
const SOURCE_W = 448;
const SOURCE_H = 252;
const SOURCE_FPS = 24;
/**
 * Measured from the PNG sequence: a planted paw moves 8 source px per frame
 * (median over ~80 ground-contact samples). The crossing speed is derived from
 * it (see `walkSpeed`) so the ground moves exactly as fast as the paws push —
 * any other speed reads as sliding.
 */
const STRIDE_PX_PER_FRAME = 8;
/**
 * Rendered video footprint. Source is 448x252 (~1.78:1); the aspect ratio is
 * preserved at 213x120 on screen, or 156x88 on phones (isTrueMobile: a mobile UA
 * or a viewport up to 1024px wide) so the cat takes ~40% of a 390px screen rather than ~55%.
 * Re-resolved from the live viewport on resize / orientation change.
 */
const CAT_H_DESKTOP = 120;
const CAT_H_PHONE = 88;

interface CatSize {
  w: number;
  h: number;
}

function catSize(): CatSize {
  const h = isTrueMobile() ? CAT_H_PHONE : CAT_H_DESKTOP;
  return { w: Math.round((h * SOURCE_W) / SOURCE_H), h };
}

/** Current footprint; set in mount(), refreshed by the resize listener. */
let size: CatSize = { w: Math.round((CAT_H_DESKTOP * SOURCE_W) / SOURCE_H), h: CAT_H_DESKTOP };

/**
 * Travel speed in px per second that matches the walk cycle at the current
 * footprint: 8 px/frame x 24 fps = 192 source px/s, scaled by how much of the
 * 448 px source is on screen — about 91 px/s for the 213 px desktop cat and
 * 67 px/s for the 156 px phone cat. Reads `size`, so it follows the
 * phone/desktop swap on resize for free.
 */
function walkSpeed(): number {
  return (STRIDE_PX_PER_FRAME * SOURCE_FPS * size.w) / SOURCE_W;
}
/**
 * Transparent padding below the paws in the source frame: they sit at y≈240
 * of 252, so ~5% of the clip height is empty under the feet. walkYFor pushes
 * the cat down by this much so the paws land on the bar's top edge.
 */
const FEET_PADDING_RATIO = 0.05;
/** The paw edge is anti-aliased; sinking 2px makes the paws read as touching the bar. */
const FEET_SINK_PX = 2;

/** Minimum bar width (px) for it to be worth walking along. */
const MIN_LINE_WIDTH = 160;
/**
 * The navigation bar's stable public class (NavigationWrapper in
 * features/navigation) and the status bar balance readout's test id
 * (testIds.statusBar.balance) — the status bar container's own class is a
 * hashed CSS module, so it is found by climbing from the balance to the first
 * full-width ancestor. Both are stable public hooks resolved by DOM query, so
 * shared/ needs no import from features/navigation.
 */
const NAV_SELECTOR = '.egt_navigation_container';
const STATUS_BAR_BALANCE_SELECTOR = '[data-testid="egt_lobby_status_bar_balance"]';
/** Delay (ms) before the very first entrance after mount, and after a pause lifts. */
const FIRST_ENTRY_MS = 1500;
/** How long (ms) the cat stays off screen between crossings: random in [min, max]. */
const HIDDEN_MIN_MS = 4000;
const HIDDEN_MAX_MS = 9000;
/** How often (ms) to re-scan for a visible bar while waiting to re-enter. */
const IDLE_POLL_MS = 600;
/**
 * How often (ms) the `elementFromPoint` hit test per bar is refreshed while
 * walking. Bar rects are read every LINE_READ_EVERY_FRAMES frames (that is
 * what lets her ride the sliding nav without stepping); only "did an overlay
 * just cover the bar" is sampled at this rate — a 200 ms latency there is
 * invisible.
 */
const HIT_TEST_MS = 200;
/**
 * While walking, the bar rects (`getBoundingClientRect` on the nav and the
 * status bar — two forced layouts) are re-read only every Nth frame; in
 * between she keeps the last line. Every entry starts with a fresh read, so
 * at worst she lags the sliding nav by two frames (~33 ms at 60 Hz).
 */
const LINE_READ_EVERY_FRAMES = 3;

/** Direction of travel across the screen. */
type WalkDir = 'toLeft' | 'toRight';

/** The compositor-driven X animation of the current crossing (see `startCrossing`). */
interface Crossing {
  anim: Animation;
  /** Start and end X (px); `anim` runs linearly from one to the other over `duration` ms. */
  from: number;
  to: number;
  duration: number;
}

interface CatRuntime {
  /** `walking`: crossing the screen. `hidden`: off screen, waiting to re-enter. */
  mode: 'walking' | 'hidden';
  x: number;
  y: number;
  /**
   * scaleX multiplier on the wrapper. Source video faces RIGHT, so facing=1
   * (un-mirrored) faces right and facing=-1 (mirrored) faces left.
   */
  facing: number;
  /** Direction of the current walk, or of the next one while hidden. */
  dir: WalkDir;
  /** While walking with Web Animations support: the running X animation. */
  crossing: Crossing | null;
  lastFrame: number;
  rafId: number;
  /** Frames walked since entry; the bar rects are re-read when it hits a multiple of LINE_READ_EVERY_FRAMES. */
  walkFrame: number;
  /** While hidden: the pending re-entry (or re-scan) timeout. */
  entryTimer: number;
  /** prefers-reduced-motion: placed once as a still frame, nothing animates. */
  still: boolean;
}

let runtime: CatRuntime | null = null;
let catEl: HTMLElement | null = null;
/** The `.cat-x` wrapper inside `catEl`: carries translateX / scaleX. */
let catXEl: HTMLElement | null = null;
/** `Element.animate` available: X runs on the compositor; otherwise the rAF loop integrates it. */
let useWebAnimations = false;
/**
 * Interaction listener that re-issues a refused play() for the current entry.
 * Attached for the overlay's lifetime (every entry's play() can be refused,
 * not just the first) and detached in destroy(); a no-op while already playing.
 */
let resumePlayback: (() => void) | null = null;
/** Viewport listener that swaps the phone/desktop footprint; detached in destroy(). */
let onResize: (() => void) | null = null;
/** Tab visibility listener; detached in destroy(). */
let onVisibilityChange: (() => void) | null = null;
/** Video `playing` listener that reveals the cat and starts the walk loop; detached in destroy(). */
let onPlaying: (() => void) | null = null;
/** `setPaused` input (the idle manager); combined with tab visibility into `effectivePaused`. */
let externalPaused = false;
let effectivePaused = false;

interface CatSource {
  url: string;
  type: string;
}

/**
 * WebKit — Safari on macOS and every browser on iOS (all WebKit there) —
 * decodes VP9 WebM without its alpha plane, so the transparent background
 * paints as an opaque black box. WebKit does render HEVC with Apple's alpha
 * layer, which Chromium in turn plays opaque. Neither engine rejects the
 * other's format, so `<source>` fallbacks would pick the wrong one; the engine
 * is detected instead. `navigator.vendor` is exactly "Apple Computer, Inc." on
 * WebKit and "Google Inc." / "" elsewhere.
 */
function isWebKitEngine(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.vendor.indexOf('Apple') === 0;
  } catch {
    return false;
  }
}

function pickSource(sources: CatSources): CatSource {
  return isWebKitEngine() ? { url: sources.hevc, type: 'video/quicktime' } : { url: sources.webm, type: 'video/webm' };
}

/** The clip picked at mount; every entry re-attaches this one (no re-pick). Cleared in destroy(). */
let pickedSource: CatSource | null = null;

/** Hand the picked clip to the video as a fresh `<source>` (the previous one is gone after `releaseVideo`). */
function attachSource(video: HTMLVideoElement): void {
  if (!pickedSource) return;
  const source = document.createElement('source');
  source.src = pickedSource.url;
  source.type = pickedSource.type;
  video.appendChild(source);
}

/**
 * Release the decoder: pause, drop the `<source>` and load() so the engine
 * frees the alpha-video pipeline (tens of MB on iOS) rather than keeping it
 * warm for a paused clip. The `playing` listener lives on the element, so it
 * survives; `attachSource` + load() brings the clip back.
 */
function releaseVideo(video: HTMLVideoElement): void {
  video.pause();
  video.innerHTML = '';
  video.removeAttribute('src');
  video.load();
}

function viewport(): { w: number; h: number } {
  const w = typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : 1280;
  const h = typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : 720;
  return { w, h };
}

/** Cached status bar container; re-resolved when it leaves the document. */
let statusBarEl: HTMLElement | null = null;
/** Cached navigation bar; re-resolved when it leaves the document. */
let navEl: HTMLElement | null = null;

function resolveNav(): HTMLElement | null {
  if (navEl && navEl.isConnected) return navEl;
  const nav = document.querySelector(NAV_SELECTOR);
  navEl = nav instanceof HTMLElement ? nav : null;
  return navEl;
}

/**
 * The status bar container: climb from the balance readout to the first
 * ancestor spanning (nearly) the full viewport width. Cached between frames.
 */
function resolveStatusBar(): HTMLElement | null {
  if (statusBarEl && statusBarEl.isConnected) return statusBarEl;
  statusBarEl = null;
  const balance = document.querySelector(STATUS_BAR_BALANCE_SELECTOR);
  if (!(balance instanceof HTMLElement)) return null;
  const { w } = viewport();
  let node: HTMLElement | null = balance.parentElement;
  while (node && node !== document.body) {
    if (node.getBoundingClientRect().width >= w * 0.9) {
      statusBarEl = node;
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** True when a bar's top edge is on screen and it is wide enough to walk along. */
function lineUsable(rect: DOMRect): boolean {
  const { h } = viewport();
  return rect.width >= MIN_LINE_WIDTH && rect.top > 0 && rect.top < h;
}

/** Per-bar hit-test cache (see `lineExposed` / HIT_TEST_MS). */
interface HitSample {
  el: HTMLElement | null;
  at: number;
  exposed: boolean;
}

const hitSamples: Record<'nav' | 'status', HitSample> = {
  nav: { el: null, at: -Infinity, exposed: false },
  status: { el: null, at: -Infinity, exposed: false },
};

function resetHitSamples(): void {
  hitSamples.nav = { el: null, at: -Infinity, exposed: false };
  hitSamples.status = { el: null, at: -Infinity, exposed: false };
}

/**
 * True when the bar is really what's on screen along its top edge, i.e. not
 * covered by an overlay (search, a modal). The nav's own `visibility` can't be
 * trusted for that: Lobby passes `visibility:hidden` while search is open, but
 * Navigation's memo comparator ignores `style`, so the inline value never
 * changes and the nav is merely painted under the search container. A hit test
 * just below the top edge, at the bar's horizontal centre, settles it; the cat
 * root is `pointer-events:none`, so `elementFromPoint` never returns the cat.
 * The result is cached per bar for HIT_TEST_MS (the rect itself is not).
 */
function lineExposed(bar: 'nav' | 'status', el: HTMLElement, rect: DOMRect): boolean {
  const now = performance.now();
  const sample = hitSamples[bar];
  if (sample.el === el && now - sample.at < HIT_TEST_MS) return sample.exposed;

  const { w, h } = viewport();
  const x = Math.max(0, Math.min(w - 1, rect.left + rect.width / 2));
  const y = Math.max(0, Math.min(h - 1, rect.top + 2));
  const hit = document.elementFromPoint(x, y);
  const exposed = hit !== null && el.contains(hit);
  hitSamples[bar] = { el, at: now, exposed };
  return exposed;
}

/** Cat-top Y so its feet rest on the bar's top edge, clamped on-screen. */
function walkYFor(rect: DOMRect): number {
  const { h } = viewport();
  const feetOnBar = rect.top - size.h + Math.round(size.h * FEET_PADDING_RATIO) + FEET_SINK_PX;
  return Math.max(0, Math.min(h - size.h, feetOnBar));
}

/**
 * The line the cat walks on right now: the top of the navigation bar, or the
 * top of the status bar once the nav is gone — hidden, covered by an overlay
 * (the search view sits over the nav but leaves the status bar visible below
 * it; see `lineExposed`), or slid down under the status bar while the user
 * scrolls on a phone. The hand-over happens as soon as the sliding nav's top
 * passes the status bar's top, so the cat rides the nav down and then settles
 * on the status bar without a jump. Returns null when neither bar is on screen
 * and exposed.
 */
function resolveWalkLine(): number | null {
  const statusBar = resolveStatusBar();
  const statusRect = statusBar ? statusBar.getBoundingClientRect() : null;
  const statusUsable = statusRect !== null && lineUsable(statusRect);

  const nav = resolveNav();
  // The visibility check stays: cheap, and correct once the memo gap is closed.
  if (nav && nav.style.visibility !== 'hidden') {
    const navRect = nav.getBoundingClientRect();
    const aboveStatusBar = statusRect === null || !statusUsable || navRect.top <= statusRect.top;
    if (lineUsable(navRect) && aboveStatusBar && lineExposed('nav', nav, navRect)) return walkYFor(navRect);
  }

  if (statusBar && statusRect && statusUsable && lineExposed('status', statusBar, statusRect)) {
    return walkYFor(statusRect);
  }
  return null;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function hiddenDuration(): number {
  return HIDDEN_MIN_MS + Math.random() * (HIDDEN_MAX_MS - HIDDEN_MIN_MS);
}

function buildCat(sources: CatSources): HTMLElement {
  const cat = document.createElement('div');
  cat.className = CAT_CLASS;
  // Two compositor layers: the outer carries Y (written per frame), the inner
  // carries X + facing (a compositor animation); everything else is static.
  cat.style.position = 'absolute';
  cat.style.left = '0';
  cat.style.top = '0';
  cat.style.willChange = 'transform';
  cat.style.visibility = 'hidden';

  const catX = document.createElement('div');
  catX.className = CAT_X_CLASS;
  catX.style.width = '100%';
  catX.style.height = '100%';
  catX.style.willChange = 'transform';

  const video = document.createElement('video');
  pickedSource = pickSource(sources);
  attachSource(video);
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.setAttribute('disablepictureinpicture', '');
  video.style.display = 'block';
  video.style.width = '100%';
  video.style.height = '100%';
  video.style.objectFit = 'contain';
  video.style.pointerEvents = 'none';
  video.style.userSelect = 'none';
  video.style.setProperty('-webkit-user-select', 'none');
  video.style.setProperty('-webkit-user-drag', 'none');

  catX.appendChild(video);
  cat.appendChild(catX);
  applySize(cat);
  return cat;
}

/** Width/height live inline so they can follow the viewport. */
function applySize(cat: HTMLElement): void {
  cat.style.width = `${size.w}px`;
  cat.style.height = `${size.h}px`;
}

function applyY(cat: HTMLElement, r: CatRuntime): void {
  cat.style.transform = `translateY(${r.y}px)`;
}

/**
 * Write both axes inline. Never called while a crossing animation is running
 * on `.cat-x` — overwriting the animated property would restart the compositor
 * animation (and the inline value is hidden under the animation anyway).
 */
function applyTransform(cat: HTMLElement, r: CatRuntime): void {
  applyY(cat, r);
  // The video is drawn facing RIGHT; facing=1 leaves it un-mirrored (walking
  // right) and facing=-1 mirrors it (walking left) so it always faces travel.
  if (catXEl) catXEl.style.transform = `translateX(${r.x}px) scaleX(${r.facing})`;
}

function setCatVisible(cat: HTMLElement, visible: boolean): void {
  cat.style.visibility = visible ? 'visible' : 'hidden';
}

function catVideo(cat: HTMLElement): HTMLVideoElement | null {
  const video = cat.querySelector('video');
  return video instanceof HTMLVideoElement ? video : null;
}

/**
 * muted inline playback is permitted, but call play() defensively and swallow
 * the rejection some engines throw before the user has interacted.
 */
function playVideo(video: HTMLVideoElement): void {
  const playback = video.play();
  if (playback && typeof playback.catch === 'function') {
    playback.catch(() => undefined);
  }
}

function stopLoop(r: CatRuntime): void {
  if (r.rafId) {
    window.cancelAnimationFrame(r.rafId);
    r.rafId = 0;
  }
  r.lastFrame = 0;
}

function clearEntryTimer(r: CatRuntime): void {
  if (r.entryTimer) {
    window.clearTimeout(r.entryTimer);
    r.entryTimer = 0;
  }
}

function cancelCrossing(r: CatRuntime): void {
  const c = r.crossing;
  if (!c) return;
  r.crossing = null;
  c.anim.cancel();
}

/** Where the crossing's X is right now (px), from the animation's own clock. */
function crossingX(c: Crossing): number {
  if (c.duration <= 0) return c.to;
  const t = c.anim.currentTime;
  const ms = typeof t === 'number' ? t : 0;
  return c.from + (c.to - c.from) * Math.min(1, Math.max(0, ms / c.duration));
}

/**
 * Start (paused) the compositor-driven X animation of a crossing: from `r.x`
 * to the first fully-off-screen position at `walkSpeed()`, linear, so the
 * ground moves exactly as fast as the paws push — the same distance / speed the
 * rAF loop used to integrate. Released by `startLoop` once the video is
 * advancing. On finish, the same hand-over the loop did: flip direction, hide,
 * schedule the re-entry. `goHidden` may cancel it first, hence the guards.
 * The distance is the signed remaining travel in the walk direction, clamped
 * at 0: when a resize restart finds her already past the new far edge the
 * animation is 0 ms and finishes (hides her) as soon as it plays, instead of
 * walking her backwards to the edge. On the entry path `from` is always the
 * off-screen edge, so the distance there is w + size.w.
 */
function startCrossing(cat: HTMLElement, catX: HTMLElement, r: CatRuntime): void {
  cancelCrossing(r);
  const { w } = viewport();
  const from = r.x;
  const to = r.dir === 'toRight' ? w : -size.w;
  const remaining = Math.max(0, (to - from) * (r.dir === 'toRight' ? 1 : -1));
  const duration = (remaining / walkSpeed()) * 1000;
  const anim = catX.animate(
    [
      { transform: `translateX(${from}px) scaleX(${r.facing})` },
      { transform: `translateX(${to}px) scaleX(${r.facing})` },
    ],
    { duration, easing: 'linear', fill: 'forwards' },
  );
  anim.pause();
  const crossing: Crossing = { anim, from, to, duration };
  anim.onfinish = () => {
    if (r.crossing !== crossing || r.mode !== 'walking') return;
    r.x = to;
    r.dir = r.dir === 'toRight' ? 'toLeft' : 'toRight';
    goHidden(cat, r);
    scheduleEntry(cat, r, hiddenDuration());
  };
  r.crossing = crossing;
}

/** Park the cat off screen, release the video (source and decoder) and stop the frame loop. */
function goHidden(cat: HTMLElement, r: CatRuntime): void {
  r.mode = 'hidden';
  stopLoop(r);
  cancelCrossing(r);
  setCatVisible(cat, false);
  const video = catVideo(cat);
  if (!video) return;
  // The reduced-motion still frame IS the loaded clip; releasing it would blank her.
  if (r.still) video.pause();
  else releaseVideo(video);
}

/** While hidden: try to enter after `delay` ms (nothing runs in between). */
function scheduleEntry(cat: HTMLElement, r: CatRuntime, delay: number): void {
  clearEntryTimer(r);
  r.entryTimer = window.setTimeout(() => {
    r.entryTimer = 0;
    if (effectivePaused || r.mode !== 'hidden') return;
    if (!tryEnter(cat, r)) {
      // Nothing to walk along yet — stay hidden and re-scan shortly.
      scheduleEntry(cat, r, IDLE_POLL_MS);
    }
  }, delay);
}

/**
 * Start a crossing along the nav/status bar, entering from the edge that
 * `r.dir` walks away from. Returns false (and stays hidden) when neither bar
 * is on screen right now. The cat is positioned, the clip handed back to the
 * video (every hide releases it — this is the only place it is restored, for
 * the crossing-finished, idle-pause and hidden-tab paths alike) and asked to
 * play; she becomes visible and the frame loop starts when `playing` fires.
 */
function tryEnter(cat: HTMLElement, r: CatRuntime): boolean {
  const y = resolveWalkLine();
  if (y === null) return false;

  const { w } = viewport();
  r.mode = 'walking';
  r.y = y;
  // Fresh read above; the loop's first frame reads again, then every Nth.
  r.walkFrame = 0;
  // Source faces RIGHT: moving right -> 1 (un-mirrored), left -> -1 (mirrored).
  r.facing = r.dir === 'toRight' ? 1 : -1;
  r.x = r.dir === 'toRight' ? -size.w : w;
  applyTransform(cat, r);
  if (useWebAnimations && catXEl) startCrossing(cat, catXEl, r);
  const video = catVideo(cat);
  if (video) {
    // Only the very first entry still has buildCat's source (and its preload);
    // after a release the clip is 143 KB and cached, so load() is just the wait
    // for `playing`. A refused play() here is retried by `resumePlayback`.
    if (!video.querySelector('source')) {
      attachSource(video);
      video.load();
    }
    playVideo(video);
  }
  return true;
}

/**
 * Drive one walking frame: follow the nav/status bar line while one is on
 * screen and keep the last line when neither is. The line is re-read (two
 * forced layouts) only every LINE_READ_EVERY_FRAMES frames; the hit test
 * inside keeps its own HIT_TEST_MS clock. With a crossing animation
 * running only Y is written (X lives on the compositor). Without one — the
 * `Element.animate` fallback — X is also advanced here until the cat has fully
 * left through the far edge, at which point it hides for a while and flips
 * direction so the next entrance comes back through the same edge.
 */
function runWalk(cat: HTMLElement, r: CatRuntime, dt: number): void {
  if (r.walkFrame % LINE_READ_EVERY_FRAMES === 0) {
    const y = resolveWalkLine();
    if (y !== null) r.y = y;
  }
  r.walkFrame += 1;

  if (r.crossing) {
    applyY(cat, r);
    return;
  }

  const { w } = viewport();
  const sign = r.dir === 'toRight' ? 1 : -1;
  r.x += sign * walkSpeed() * dt;
  applyTransform(cat, r);

  const fullyOff = r.dir === 'toRight' ? r.x >= w : r.x <= -size.w;
  if (fullyOff) {
    r.dir = r.dir === 'toRight' ? 'toLeft' : 'toRight';
    goHidden(cat, r);
    scheduleEntry(cat, r, hiddenDuration());
  }
}

function step(now: number): void {
  const r = runtime;
  const cat = catEl;
  if (!r || !cat || r.mode !== 'walking') return;

  const dt = r.lastFrame > 0 ? Math.min((now - r.lastFrame) / 1000, 0.1) : 0;
  r.lastFrame = now;

  runWalk(cat, r, dt);
  if (r.mode === 'walking') {
    r.rafId = window.requestAnimationFrame(step);
  }
}

/**
 * The video is actually advancing: reveal the cat, release the X animation
 * and run the walk loop. `playing` can fire again mid-walk (after a stall), so
 * only a still-paused crossing is released — play() on a finished animation
 * would restart it from the entry edge.
 */
function startLoop(cat: HTMLElement, r: CatRuntime): void {
  if (r.mode !== 'walking' || effectivePaused) return;
  setCatVisible(cat, true);
  const c = r.crossing;
  if (c && c.anim.playState === 'paused') c.anim.play();
  if (!r.rafId) {
    r.lastFrame = 0;
    r.rafId = window.requestAnimationFrame(step);
  }
}

/**
 * Recompute the effective pause (external `setPaused` OR hidden tab) and act
 * on a change: pausing hides the cat, pauses the video and stops every loop /
 * timer; resuming re-enters after the usual first-entry beat. The
 * reduced-motion still frame just hides and shows.
 */
function applyPauseState(): void {
  const next = externalPaused || (typeof document !== 'undefined' && document.hidden);
  if (next === effectivePaused) return;
  effectivePaused = next;

  const r = runtime;
  const cat = catEl;
  if (!r || !cat) return;

  if (r.still) {
    // The still frame was placed (x moved from the off-screen start to 0) only
    // when a bar was found at mount; otherwise there is nothing to show.
    const placed = r.x === 0;
    setCatVisible(cat, !next && placed);
    return;
  }

  if (next) {
    clearEntryTimer(r);
    goHidden(cat, r);
  } else {
    scheduleEntry(cat, r, FIRST_ENTRY_MS);
  }
}

/**
 * Pause / resume from the outside (App.tsx's idle manager via the `paused`
 * prop). Safe to call before mount(); the flag is read when mounting.
 */
export function setPaused(paused: boolean): void {
  externalPaused = paused;
  applyPauseState();
}

/**
 * Build the cat inside `root` and start the show. `root` is HalloweenCat's
 * fixed, pointer-transparent container; only the `.halloween-cat` child is
 * ours to add and remove.
 */
export function mount(root: HTMLElement, sources: CatSources): void {
  if (catEl) return;

  size = catSize();
  resetHitSamples();
  const cat = buildCat(sources);
  root.appendChild(cat);
  catEl = cat;
  const catX = cat.querySelector(`.${CAT_X_CLASS}`);
  catXEl = catX instanceof HTMLElement ? catX : null;
  useWebAnimations = catXEl !== null && typeof catXEl.animate === 'function';

  const { h } = viewport();
  runtime = {
    mode: 'hidden',
    x: -size.w,
    y: Math.max(0, h - size.h),
    // Source faces RIGHT (un-mirrored), matching the first crossing below.
    facing: 1,
    // First crossing enters from the left and walks right.
    dir: 'toRight',
    crossing: null,
    lastFrame: 0,
    rafId: 0,
    walkFrame: 0,
    entryTimer: 0,
    still: false,
  };
  const r = runtime;
  const video = catVideo(cat);

  effectivePaused = externalPaused || document.hidden;
  onVisibilityChange = applyPauseState;
  document.addEventListener('visibilitychange', onVisibilityChange);

  if (prefersReducedMotion()) {
    // Respect the user preference: place the cat once as a still frame on
    // the nav/status bar (or leave it hidden) rather than running a
    // perpetual rAF loop or video.
    r.still = true;
    if (video) video.pause();
    const y = resolveWalkLine();
    if (y !== null) {
      r.y = y;
      r.x = 0;
      setCatVisible(cat, !effectivePaused);
    }
    applyTransform(cat, r);
    return;
  }

  // Start off screen; the first entrance comes after a short beat.
  applyTransform(cat, r);
  if (!effectivePaused) scheduleEntry(cat, r, FIRST_ENTRY_MS);

  // Phone <-> desktop footprint follows the viewport (resize also fires on
  // orientation change). Only the box is re-applied here; the next frame
  // re-reads Y from walkYFor and the off-screen bounds from `size`. A running
  // crossing is restarted from where she is now, at the new speed and towards
  // the new far edge — the rAF loop used to pick both up live, frame by frame.
  onResize = () => {
    const next = catSize();
    const sizeChanged = next.h !== size.h;
    if (sizeChanged) {
      size = next;
      applySize(cat);
    }
    const c = r.crossing;
    if (!c || !catXEl || r.mode !== 'walking' || c.anim.playState === 'finished') return;
    const to = r.dir === 'toRight' ? viewport().w : -size.w;
    if (!sizeChanged && to === c.to) return;
    r.x = crossingX(c);
    const running = c.anim.playState === 'running';
    startCrossing(cat, catXEl, r);
    if (running && r.crossing) r.crossing.anim.play();
  };
  window.addEventListener('resize', onResize, { passive: true });

  if (video) {
    // Never show a frozen first frame: the cat is revealed (and the frame
    // loop started) only once the video reports it is advancing.
    onPlaying = () => startLoop(cat, r);
    video.addEventListener('playing', onPlaying);

    // iOS Low Power Mode refuses autoplay even for muted inline video and
    // leaves the first frame frozen; a touch on the page unlocks playback, so
    // retry then. Every entry issues a fresh play() (the clip is reloaded after
    // each hide) and any of them can be refused, so the listener stays armed
    // for the overlay's lifetime; the guard makes it a no-op per tap while she
    // is playing, hidden (the video is meant to be released then) or paused.
    resumePlayback = () => {
      if (video.paused && r.mode === 'walking' && !effectivePaused) playVideo(video);
    };
    document.addEventListener('pointerdown', resumePlayback, { passive: true });
    document.addEventListener('touchend', resumePlayback, { passive: true });
  }
}

function detachResumePlayback(): void {
  if (!resumePlayback) return;
  document.removeEventListener('pointerdown', resumePlayback);
  document.removeEventListener('touchend', resumePlayback);
  resumePlayback = null;
}

export function destroy(): void {
  const r = runtime;
  if (r) {
    stopLoop(r);
    clearEntryTimer(r);
    cancelCrossing(r);
  }
  runtime = null;
  statusBarEl = null;
  navEl = null;
  resetHitSamples();
  detachResumePlayback();
  if (onResize) {
    window.removeEventListener('resize', onResize);
    onResize = null;
  }
  if (onVisibilityChange) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    onVisibilityChange = null;
  }
  const cat = catEl;
  catEl = null;
  catXEl = null;
  useWebAnimations = false;
  if (cat) {
    // Release the video decoder before detaching.
    const video = catVideo(cat);
    if (video) {
      if (onPlaying) video.removeEventListener('playing', onPlaying);
      releaseVideo(video);
    }
    if (cat.parentNode) {
      cat.parentNode.removeChild(cat);
    }
  }
  pickedSource = null;
  onPlaying = null;
  effectivePaused = false;
}
